import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../Common';
import Input from '@ajat/design-system/Input';
import Price from '@ajat/design-system/Price';
import ProductCard from '@ajat/design-system/ProductCard';
import {
  HeadingSmall,
  HeadingMedium,
  StickyFooter,
} from '@ajat/design-system/Configurator';
import InfoTooltip from '@ajat/design-system/InfoTooltip';
import Preloader from '@ajat/design-system/Preloader';
import Gallery from '../../Common/Product/Gallery';
import AttributeSelector from '../../Common/Product/AttributeSelector';
import DynamicAttributeControls from '../../Common/Product/DynamicAttributeControls';
import {
  resolveConstraintGroups, propagateConstraint, reconcileConstraints, unavailableLinked as constraintUnavailable,
} from '../../Common/Product/constraints/attributeConstraints';
import CLSAddons from './CLSAddons';
import SizeGuide from '../../Common/Product/SizeGuide';
import { DeliveryBlock } from '@ajat/design-system/SharedBlocks';
import useProductPage from '../../Common/Product/useProductPage';
import ClosedShopMessage from '../../Common/ClosedShop/ClosedShopMessage';
import SchoolClassGate from '../../Common/SchoolClassGate';
import { resolveShopDestination } from '../../../../services/shopDestination';
import usePackageVariation from '../../../../hooks/usePackageVariation';
import useSchoolSelection from '../../../../hooks/useSchoolSelection';
import { readFreshSelection } from '../../../../services/schoolSelectionTtl';
import { cartScopeToBind } from '../../../../services/cartScope';
import { STORAGE_KEYS } from '../../../../hooks/useMenuItemNavigation';
import useForceSchoolSelection from '../../../../hooks/useForceSchoolSelection';
import useCompatibilityTree, { isSelectionSoldOut } from '../../../../hooks/useCompatibilityTree';
import useControlVisibility, { resolveHiddenControlSlugs, findUnansweredRevealedSlugs } from '../../../../hooks/useControlVisibility';
import { useCart } from '../../../../context/CartContext';
import ThreeDGallery, { threeDSupported } from '../../Common/Product/ThreeDGallery';
import useVisualViewportVars from '../../Common/Product/useVisualViewportVars';
import MobileFocusModal from '@ajat/design-system/MobileFocus';
import { buildTextfieldFocusFields, EMPTY_FOCUS_FIELD } from '../../Common/Product/textfieldFocusFields';
import { buildThreeDProps } from '../../Common/Product/threeD/buildThreeDProps';
import useThreeDCamera from '../../Common/Product/threeD/useThreeDCamera';
import { smoothScrollToTop } from '../../../../utils/scroll';
import { SIZE_GUIDE_TITLE, SIZE_GUIDE_ITEMS } from './sizeGuideData';
import '../../Common/Product/ProductDetails.css';
import '../../Common/Product/ProductPackage.css';

const BRAND_SLUG = 'clsfi';

const emptyConfig = () => ({
  selectedProduct: null,
  selectedColor: 0,
  selectedSize: null,
  selectedAttributes: {},
});

// Unified slug→valueId selection map for a product+config (color by index,
// size by value string, the rest already keyed by value id), used to evaluate
// the compatibility tree and control-visibility rules.
const unifiedSelectionsFor = (product, config) => {
  const out = { ...(config?.selectedAttributes || {}) };
  const colorAttr = (product?.attributes || []).find((a) => a.slug === 'color');
  const sizeAttr  = (product?.attributes || []).find((a) => a.slug === 'size');
  const colorId = colorAttr?.values?.[config?.selectedColor]?.id;
  const sizeId  = sizeAttr?.values?.find((v) => v.value === config?.selectedSize)?.id;
  if (colorId != null) out.color = colorId;
  if (sizeId  != null) out.size  = sizeId;
  return out;
};

// Hidden controls (disabled) for a product given its config — never priced/saved.
const hiddenSlugsFor = (product, config) => resolveHiddenControlSlugs({
  hiddenSlugs: product?.hidden_attributes ?? [],
  rules:       product?.visibility_rules ?? [],
  selections:  unifiedSelectionsFor(product, config),
});

const productAddtl = (product, config, hiddenSet = new Set()) => {
  if (!product) return 0;
  return (product.attributes || []).reduce((sum, a) => {
    if (hiddenSet.has(a.slug)) return sum; // hidden control = disabled, not priced
    const sel = config.selectedAttributes?.[a.slug];
    if (a.type === 'textfield') {
      if (typeof sel !== 'string' || sel.trim() === '') return sum;
      return sum + (Number(a.values?.[0]?.additional_price) || 0);
    }
    const match = a.values?.find((v) => v.id === sel);
    return sum + (Number(match?.additional_price) || 0);
  }, 0);
};

/** Class option label of the read-only mode's school + class picker. */
const fiClassLabel = (c) => `Luokka ${c}`;

const ProductPackage = () => {
  const { slug, schoolSlug: routeSchoolSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const editOrder = location.state?.editOrder ?? null;
  // Cart-line edit (from the shopping cart): pre-populate like editOrder,
  // but saving replaces the CART line (remove + re-add) instead of an order.
  const editCart = location.state?.editCart ?? null;

  const { schoolSlug: savedSchoolSlug, className: savedClassName, shopSlug: savedShopSlug } = useSchoolSelection(BRAND_SLUG);
  // School-scoped route (/school-shop/:schoolSlug/product-package/:slug?shop=)
  // mirrors product links: the URL context wins over the persisted selection,
  // so shared/bookmarked links price correctly without localStorage. The stored
  // class/shop only apply when they belong to the SAME school as the URL.
  const schoolSlug = routeSchoolSlug ?? savedSchoolSlug;
  const sameSchool = !routeSchoolSlug || routeSchoolSlug === savedSchoolSlug;
  const shopSlug = new URLSearchParams(location.search).get('shop') ?? (sameSchool ? savedShopSlug : null);
  const className = sameSchool ? savedClassName : null;
  // READ-ONLY mode (same rule as ProductDetails): without school + class the visitor sees
  // the package (gallery, name, default price, description) but no configurator and no
  // add-to-cart — the school + class picker takes the configurator's place, no popup.
  const readOnly = !editOrder && !editCart && !(schoolSlug && className);
  // No stored school+class → force the SchoolSelector open (brand-gated).
  // Skipped for order-edit, which carries its own scope.
  const { openShopBlocked } = useForceSchoolSelection({
    brandSlug: BRAND_SLUG,
    hasSelection: Boolean(schoolSlug && className),
    // Blocking only needs the school - a missing class is not school-less.
    hasSchool: Boolean(schoolSlug),
    skip: Boolean(editOrder) || Boolean(editCart) || readOnly,
    // DK: identification (school+shop+class) is MANDATORY on product pages —
    // non-dismissible popup; the customer stays here after selecting (school
    // routes: only when the picked school matches the URL's).
    mandatory: true,
    staySchool: routeSchoolSlug || '*',
    prefill: routeSchoolSlug ? { schoolSlug: routeSchoolSlug, shopSlug: new URLSearchParams(window.location.search).get('shop') } : null,
  });
  const { data: pkg, loading, error } = usePackageVariation(BRAND_SLUG, schoolSlug, slug, className, shopSlug);
  const { addItem, removeItem, setSchool: setCartSchool, school: cartSchool, itemCount: cartItemCount } = useCart(BRAND_SLUG);

  /* Mandatory identification before add-to-cart (same contract as the PDP);
   * edit flows are exempt — they carry their own scope. */
  const requireIdentification = useCallback(() => {
    if (editOrder || editCart) return {};
    const sel = readFreshSelection(STORAGE_KEYS.clsfi);
    if (sel?.school?.id && sel?.class) return sel;
    document.dispatchEvent(new CustomEvent('open-school-selector', {
      detail: {
        mandatory: true,
        staySchool: routeSchoolSlug || '*',
        ...(routeSchoolSlug ? { prefill: { schoolSlug: routeSchoolSlug, shopSlug: new URLSearchParams(window.location.search).get('shop') } } : {}),
      },
    }));
    return null;
  }, [editOrder, editCart, routeSchoolSlug]);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const groups = pkg?.groups ?? [];
  const packageAttributes = pkg?.attributes ?? [];

  const [activeTypeIndex, setActiveTypeIndex] = useState(0);
  const [typeConfigs, setTypeConfigs] = useState({});

  useEffect(() => {
    if (!pkg) return;
    const map = {};
    pkg.groups.forEach((_, i) => { map[i] = emptyConfig(); });
    setTypeConfigs(map);
    setActiveTypeIndex(0);
    setSelectedPackageAttributes({});
  }, [pkg]);

  useEffect(() => {
    const src = editOrder ?? editCart;
    if (!src || !pkg) return;
    if (src.options?.packageAttributes && typeof src.options.packageAttributes === 'object') {
      setSelectedPackageAttributes(src.options.packageAttributes);
    }
    const included = src.includedProducts;
    if (!included?.length) return;
    const map = {};
    pkg.groups.forEach((g, i) => {
      const saved = included[i];
      if (!saved) { map[i] = emptyConfig(); return; }
      const prodIdx = g.products.findIndex((p) => p.name?.toLowerCase() === saved.productName?.toLowerCase());
      const selProduct = prodIdx >= 0 ? prodIdx : null;
      const prod = selProduct != null ? g.products[selProduct] : null;
      const colorIdx = prod?.colors?.findIndex((c) => c.name?.toLowerCase() === saved.color?.toLowerCase()) ?? -1;
      map[i] = {
        selectedProduct: selProduct,
        selectedColor: colorIdx >= 0 ? colorIdx : 0,
        selectedSize: saved.size || null,
        selectedAttributes: (saved.attributes && typeof saved.attributes === 'object') ? saved.attributes : {},
      };
    });
    setTypeConfigs(map);
  }, [editOrder, editCart, pkg]);

  const [activeImage, setActiveImage] = useState(0);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [attrInfo, setAttrInfo] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedPackageAttributes, setSelectedPackageAttributes] = useState({});

  const activeGroup = groups[activeTypeIndex];
  const config = typeConfigs[activeTypeIndex] ?? emptyConfig();
  const selectedProduct = config.selectedProduct != null ? activeGroup?.products[config.selectedProduct] : null;

  const attributes = selectedProduct?.attributes ?? [];
  const colors     = selectedProduct?.colors ?? [];
  const sizes      = selectedProduct?.sizes ?? [];
  const colorAttr  = useMemo(() => attributes.find((a) => a.slug === 'color'), [attributes]);
  const sizeAttr   = useMemo(() => attributes.find((a) => a.slug === 'size'), [attributes]);

  const unifiedSelections = useMemo(() => {
    const out = { ...(config.selectedAttributes || {}) };
    const colorId = colorAttr?.values?.[config.selectedColor]?.id;
    const sizeId  = sizeAttr?.values?.find((v) => v.value === config.selectedSize)?.id;
    if (colorId != null) out.color = colorId;
    if (sizeId  != null) out.size  = sizeId;
    return out;
  }, [config.selectedAttributes, config.selectedColor, config.selectedSize, colorAttr, sizeAttr]);

  const {
    enabled: treeEnabled,
    allowedValueIdsByAttribute,
    soldOutValueIdsByAttribute,
    currentNodeImages,
  } = useCompatibilityTree({
    tree: selectedProduct?.compatibility_tree ?? [],
    selections: unifiedSelections,
  });

  // Controls currently hidden (disabled) for the active product.
  const hiddenControlSlugs = useControlVisibility({
    hiddenSlugs: selectedProduct?.hidden_attributes ?? [],
    rules:       selectedProduct?.visibility_rules ?? [],
    selections:  unifiedSelections,
  });

  const disabledColorIdxs = useMemo(() => {
    const allowed = allowedValueIdsByAttribute.color;
    if (!allowed || !colorAttr) return new Set();
    return new Set(colorAttr.values.map((v, i) => (allowed.has(v.id) ? null : i)).filter((i) => i !== null));
  }, [allowedValueIdsByAttribute, colorAttr]);

  // Pinned color value ids → indices in `colors`, so the chips lock (a
  // variation that fixes the colour can't be changed) — parity with the PDP.
  const pinnedColorIdxs = useMemo(() => {
    const ids = selectedProduct?.pinnedColorValueIds ?? [];
    if (!ids.length || !colorAttr) return new Set();
    const set = new Set();
    colorAttr.values.forEach((val, i) => { if (ids.includes(val.id)) set.add(i); });
    return set;
  }, [selectedProduct, colorAttr]);

  const disabledSizes = useMemo(() => {
    const allowed = allowedValueIdsByAttribute.size;
    if (!allowed || !sizeAttr) return new Set();
    return new Set(sizeAttr.values.filter((v) => !allowed.has(v.id)).map((v) => v.value));
  }, [allowedValueIdsByAttribute, sizeAttr]);

  const disabledValueIdsByAttribute = useMemo(() => {
    const out = {};
    for (const attr of attributes) {
      if (attr.slug === 'color' || attr.slug === 'size') continue;
      const allowed = allowedValueIdsByAttribute[attr.slug];
      if (!allowed) continue;
      out[attr.slug] = new Set(attr.values.filter((v) => !allowed.has(v.id)).map((v) => v.id));
    }
    return out;
  }, [allowedValueIdsByAttribute, attributes]);

  // Sold-out combos: hidden entirely (not just disabled).
  const soldOutColorIdxs = useMemo(() => {
    const soldOut = soldOutValueIdsByAttribute.color;
    if (!soldOut || !colorAttr) return new Set();
    return new Set(colorAttr.values.map((v, i) => (soldOut.has(v.id) ? i : null)).filter((i) => i !== null));
  }, [soldOutValueIdsByAttribute, colorAttr]);

  const soldOutSizes = useMemo(() => {
    const soldOut = soldOutValueIdsByAttribute.size;
    if (!soldOut || !sizeAttr) return new Set();
    return new Set(sizeAttr.values.filter((v) => soldOut.has(v.id)).map((v) => v.value));
  }, [soldOutValueIdsByAttribute, sizeAttr]);

  // ANY group's current selection landing on a sold-out combo blocks the
  // package CTA (the hook above only walks the active group's tree).
  const anySelectionSoldOut = useMemo(() => groups.some((g, i) => {
    const cfg = typeConfigs[i];
    const prod = cfg?.selectedProduct != null ? g.products[cfg.selectedProduct] : null;
    if (!prod) return false;
    const cAttr = (prod.attributes ?? []).find((a) => a.slug === 'color');
    const sAttr = (prod.attributes ?? []).find((a) => a.slug === 'size');
    const sels = { ...(cfg.selectedAttributes || {}) };
    const colorId = cAttr?.values?.[cfg.selectedColor]?.id;
    const sizeId = sAttr?.values?.find((v) => v.value === cfg.selectedSize)?.id;
    if (colorId != null) sels.color = colorId;
    if (sizeId != null) sels.size = sizeId;
    return isSelectionSoldOut(prod.compatibility_tree ?? [], sels);
  }), [groups, typeConfigs]);


  // 3D layout for the active group product (school-scoped members carry their
  // variation's layout + mapping); other members fall back to photos.
  const threeD = useMemo(() => buildThreeDProps(selectedProduct?.layout, selectedProduct?.three_d, {
    attributes: selectedProduct?.attributes ?? [],
    selectedAttributes: config.selectedAttributes || {},
    colors,
    selectedColor: config.selectedColor ?? 0,
    hiddenControlSlugs: hiddenControlSlugs,
    logoAssets: selectedProduct?.logo_assets ?? null,
    seasonYear: selectedProduct?.season_year ?? null,
  }), [selectedProduct, config.selectedAttributes, config.selectedColor, colors, hiddenControlSlugs]);
  const [threeDFailed, setThreeDFailed] = useState(false);
  const showThreeD = !!threeD && !threeDFailed && threeDSupported();
  const { threeDFocus, focusThreeD, blurThreeD } = useThreeDCamera(threeD);

  const images = selectedProduct?.images?.length
    ? selectedProduct.images
    : activeGroup?.products[0]?.images ?? pkg?.images ?? [];
  const effectiveImages = (treeEnabled && currentNodeImages.length > 0) ? currentNodeImages : images;

  const {
    isMobile, focusField, isZoomed, setIsZoomed,
    galleryRef, scrollYBeforeZoom, activeFocusContainerRef,
    handleTouchStart, handleTouchEnd, openFocusMode, closeFocusMode,
  } = useProductPage({ imageCount: effectiveImages.length, activeImage, setActiveImage, brand: 'CLS' });
  // Mobile focus mode on 3D pages: while the MobileFocusModal is open the 3D
  // column stays pinned above the card (Gallery.css .pp--typing) and sizes
  // itself to the visual viewport left over by the keyboard.
  // so both mobile editing paths size the column to the visible viewport.
  useVisualViewportVars(showThreeD && !!focusField);
  // Mobile focus mode ↔ 3D camera: on phones the inline textfield is read-only
  // and opens the MobileFocusModal instead (the trigger already plays the
  // field's camera); when the modal closes (keyboard dismissed / Done / tap
  // away) the field's blur camera plays — the inline input never blurs there.
  const prevFocusFieldRef = useRef(null);
  useEffect(() => {
    const prev = prevFocusFieldRef.current;
    prevFocusFieldRef.current = focusField;
    if (!focusField && prev) blurThreeD(prev);
  }, [focusField, blurThreeD]);

  useEffect(() => { setActiveImage(0); }, [activeTypeIndex, config.selectedProduct]);

  const updateConfig = useCallback((field, value) => {
    setTypeConfigs((prev) => ({ ...prev, [activeTypeIndex]: { ...prev[activeTypeIndex], [field]: value } }));
  }, [activeTypeIndex]);

  // Auto-select every group product's variation-PINNED values (parity with
  // ProductDetails): a pinned attribute renders locked in the configurator,
  // so it must be selected programmatically to be priced and saved on the
  // order (e.g. a package hoodie's pinned "Lille Brysttryk: Ja"). A pinned
  // color also locks the chip to the pinned index. Runs for ALL groups (not
  // just the active one) so untouched groups still save their pins.
  useEffect(() => {
    if (!pkg) return;
    setTypeConfigs((prev) => {
      let changed = false;
      const next = { ...prev };
      pkg.groups.forEach((g, i) => {
        const cfg = prev[i];
        const prod = cfg?.selectedProduct != null ? g.products[cfg.selectedProduct] : null;
        if (!prod) return;
        let cfgChanged = false;
        const attrs = { ...(cfg.selectedAttributes || {}) };
        (prod.attributes ?? []).forEach((attr) => {
          if (attr.slug === 'color' || attr.slug === 'size') return;
          if (attrs[attr.slug] != null) return;
          const pinnedId = attr.pinnedValueIds?.[0];
          if (pinnedId != null) { attrs[attr.slug] = pinnedId; cfgChanged = true; }
        });
        let selColor = cfg.selectedColor;
        const cAttr = (prod.attributes ?? []).find((a) => a.slug === 'color');
        const pinnedColorId = cAttr?.pinnedValueIds?.[0];
        if (pinnedColorId != null) {
          const idx = cAttr.values.findIndex((v) => v.id === pinnedColorId);
          if (idx >= 0 && idx !== selColor) { selColor = idx; cfgChanged = true; }
        }
        if (cfgChanged) {
          next[i] = { ...cfg, selectedAttributes: attrs, selectedColor: selColor };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pkg, typeConfigs]);

  // Clear the active group's selections that became tree-disallowed or sold
  // out (sold-out options are hidden, so a stale selection must not linger).
  useEffect(() => {
    if (!treeEnabled) return;
    if (config.selectedSize && (disabledSizes.has(config.selectedSize) || soldOutSizes.has(config.selectedSize))) {
      updateConfig('selectedSize', null);
    }
    if (config.selectedColor != null && soldOutColorIdxs.has(config.selectedColor)) {
      updateConfig('selectedColor', null);
    }
  }, [treeEnabled, disabledSizes, soldOutSizes, soldOutColorIdxs, config.selectedSize, config.selectedColor, updateConfig]);

  /* -- Linked attributes of the ACTIVE product (constraints of its 3D settings) --
     Same rules as the single product page: see attributeConstraints.js. */
  const constraintGroups = useMemo(
    () => resolveConstraintGroups(selectedProduct?.three_d, selectedProduct?.attributes ?? []),
    [selectedProduct],
  );
  const handleAttributeChange = useCallback((attrSlug, valueId) => {
    setTypeConfigs((prev) => ({
      ...prev,
      [activeTypeIndex]: {
        ...prev[activeTypeIndex],
        selectedAttributes: propagateConstraint(prev[activeTypeIndex].selectedAttributes || {}, attrSlug, valueId, constraintGroups),
      },
    }));
  }, [activeTypeIndex, constraintGroups]);
  useEffect(() => {
    if (!constraintGroups.length) return;
    setTypeConfigs((prev) => {
      const cfg = prev[activeTypeIndex];
      if (!cfg) return prev;
      const current = cfg.selectedAttributes || {};
      const synced = reconcileConstraints(current, constraintGroups, hiddenControlSlugs);
      return synced === current ? prev : { ...prev, [activeTypeIndex]: { ...cfg, selectedAttributes: synced } };
    });
  }, [constraintGroups, config.selectedAttributes, hiddenControlSlugs, activeTypeIndex]);
  const unavailableLinked = useMemo(
    () => constraintUnavailable(config.selectedAttributes || {}, constraintGroups, hiddenControlSlugs),
    [config.selectedAttributes, constraintGroups, hiddenControlSlugs],
  );

  /* -- Focus fields map for MobileFocusModal (active group's textfields, by slug) -- */
  const focusFields = buildTextfieldFocusFields({
    attributes, selectedAttributes: config.selectedAttributes || {}, onChange: handleAttributeChange, hiddenControlSlugs,
  });
  const activeFocusData = (focusField && focusFields[focusField]) || EMPTY_FOCUS_FIELD;

  const clearError = useCallback((groupIdx, field) => {
    setValidationErrors((prev) => {
      const ge = prev[groupIdx];
      if (!ge || !ge[field]) return prev;
      const next = { ...ge }; delete next[field];
      if (Object.keys(next).length === 0) { const copy = { ...prev }; delete copy[groupIdx]; return copy; }
      return { ...prev, [groupIdx]: next };
    });
  }, []);

  const handleProductSelect = (value) => {
    if (!activeGroup) return;
    const idx = activeGroup.products.findIndex((p) => p.slug === value || p.name === value);
    updateConfig('selectedProduct', idx >= 0 ? idx : null);
    updateConfig('selectedColor', 0);
    updateConfig('selectedSize', null);
    updateConfig('selectedAttributes', {});
    if (idx >= 0) clearError(activeTypeIndex, 'product');
  };
  const productOptions = activeGroup ? activeGroup.products.map((p) => ({ label: p.name, value: p.slug || p.name })) : [];

  const validate = useCallback(() => {
    if (!pkg) return false;
    const errors = {};
    pkg.groups.forEach((_, i) => {
      const cfg = typeConfigs[i];
      if (!cfg) return;
      const p = cfg.selectedProduct != null ? pkg.groups[i].products[cfg.selectedProduct] : null;
      const hidden = hiddenSlugsFor(p, cfg);
      const ge = {};
      if (cfg.selectedProduct === null) ge.product = 'Valitse tuote';
      else if (!cfg.selectedSize && !hidden.has('size')) ge.size = 'Valitse koko';
      // Revealed-by-rule detail dropdowns (letter, country) left unanswered:
      // the customer chose the paid trigger, so the detail is mandatory.
      if (p) {
        const unanswered = findUnansweredRevealedSlugs({
          attributes: p.attributes ?? [],
          hiddenSlugs: p.hidden_attributes ?? [],
          hiddenControlSlugs: hidden,
          selections: cfg.selectedAttributes ?? {},
        });
        if (unanswered.length) ge.revealed = true;
      }
      if (Object.keys(ge).length) errors[i] = ge;
    });
    setValidationErrors(errors);
    if (Object.keys(errors).length === 0) return true;
    if (Object.values(errors).some((e) => e.revealed)) {
      setShowRequiredErrors(true);
      requestAnimationFrame(() => setTimeout(() => {
        document.querySelector('[data-required-empty]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120));
    }
    const firstErrIdx = pkg.groups.findIndex((_, i) => errors[i]);
    if (firstErrIdx !== activeTypeIndex) setActiveTypeIndex(firstErrIdx);
    return false;
  }, [pkg, typeConfigs, activeTypeIndex]);

  // Unanswered revealed dropdowns of the ACTIVE group's selected product —
  // drives the inline errors once showRequiredErrors flips on.
  const activeUnansweredRevealed = useMemo(() => {
    const cfg = typeConfigs[activeTypeIndex];
    const p = cfg?.selectedProduct != null ? pkg?.groups?.[activeTypeIndex]?.products?.[cfg.selectedProduct] : null;
    if (!p) return [];
    return findUnansweredRevealedSlugs({
      attributes: p.attributes ?? [],
      hiddenSlugs: p.hidden_attributes ?? [],
      hiddenControlSlugs: hiddenSlugsFor(p, cfg),
      selections: cfg.selectedAttributes ?? {},
    });
  }, [pkg, typeConfigs, activeTypeIndex]);

  const attributesAdditional = useMemo(() => {
    if (!pkg) return 0;
    return pkg.groups.reduce((sum, g, i) => {
      const cfg = typeConfigs[i] || emptyConfig();
      const p = cfg.selectedProduct != null ? g.products[cfg.selectedProduct] : null;
      return sum + productAddtl(p, cfg, hiddenSlugsFor(p, cfg));
    }, 0);
  }, [pkg, typeConfigs]);
  const packageAttrsAdditional = useMemo(() => (packageAttributes || []).reduce((sum, a) => {
    const sel = selectedPackageAttributes[a.slug];
    if (a.type === 'textfield') {
      if (typeof sel !== 'string' || sel.trim() === '') return sum;
      return sum + (Number(a.values?.[0]?.additional_price) || 0);
    }
    const m = a.values?.find((v) => v.id === sel);
    return sum + (Number(m?.additional_price) || 0);
  }, 0), [packageAttributes, selectedPackageAttributes]);
  const totalPriceRaw = (pkg?.priceRaw ?? 0) + attributesAdditional + packageAttrsAdditional;

  // Paid-order edit: the price must stay neutral. Show the target and block
  // Update until the recomputed total matches it (mirrors ProductDetails).
  const priceLocked = !!editOrder?.priceLocked;
  const targetPrice = editOrder?.targetPrice ?? null;
  const priceMismatch = priceLocked && targetPrice != null && Math.abs(totalPriceRaw - targetPrice) > 0.5;

  // The package configurator has no vendor delivery message; the StickyFooter
  // shows the brand's one-line delivery note instead.

  const buildIncludedProducts = useCallback(() => groups.map((g, i) => {
    const c = typeConfigs[i] ?? emptyConfig();
    const p = c.selectedProduct != null ? g.products[c.selectedProduct] : null;
    // Hidden controls are disabled: drop their values from the saved order.
    const hidden = hiddenSlugsFor(p, c);
    const visibleAttrs = Object.fromEntries(
      Object.entries(c.selectedAttributes || {}).filter(([slug]) => !hidden.has(slug)),
    );
    return {
      groupName: g.name,
      productId: p?.id ?? null,
      // Mini-thumbnail follows the SELECTED colour (override-aware image_url
      // from the payload - class/shop image overrides included), like the
      // variation cards; product first image is the fallback.
      image: (!hidden.has('color') && c.selectedColor != null
        ? ((p?.attributes ?? []).find((a) => a.slug === 'color')?.values?.[c.selectedColor]?.image_url ?? null)
        : null) || p?.images?.[0]?.src || p?.images?.[0]?.url || null,
      productName: p?.name || g.name,
      color: hidden.has('color') ? null : (p?.colors?.[c.selectedColor]?.name || null),
      size: hidden.has('size') ? null : c.selectedSize,
      attributes: visibleAttrs,
      // Human-readable pairs for the cart display (ids resolved to labels
      // here, while the catalog payload is still loaded).
      attributesList: Object.entries(visibleAttrs).flatMap(([slug, val]) => {
        if (val == null || val === '') return [];
        const attr = (p?.attributes ?? []).find((a) => a.slug === slug);
        if (!attr || attr.display_only) return [];
        const label = attr.name || slug;
        if (attr.type === 'logo') return [{ label, value: 'Kyllä' }];
        const valueLabel = typeof val === 'string' ? val : (attr.values?.find((v) => v.id === val)?.value ?? String(val));
        return valueLabel ? [{ label, value: valueLabel }] : [];
      }),
    };
  }), [groups, typeConfigs]);

  /* Open shop disabled + no school on a selector-less brand → closed page. */
  // Read-only mode → school + class picked in the gate: this package in the visitor's shop
  // → the shop page when the shop does not sell it → the main page without an open shop.
  const handleGateSelected = useCallback(async ({ school, season, class: cls }) => {
    const path = await resolveShopDestination({
      brandSlug: BRAND_SLUG,
      feSlug: 'cls-fi',
      school,
      chosenShop: season,
      className: cls,
      target: { type: 'package', id: pkg?.id ?? null, slug, name: pkg?.name ?? '' },
      closedPath: '/clothing/cls-fi',
    });
    navigate(path, { replace: true });
  }, [navigate, pkg?.id, pkg?.name, slug]);

  if (openShopBlocked) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} showTopBar={false} /></div>
        <ClosedShopMessage brandSlug={BRAND_SLUG} feSlug="cls-fi" standalone />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} showTopBar={false} /></div>
        <main className="pp__main" style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><Preloader /></main>
      </div>
    );
  }
  if (error || !pkg) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} showTopBar={false} /></div>
        <main className="pp__main" style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><p>Pakettia ei löytynyt.</p></main>
      </div>
    );
  }

  return (
    <div className={`pp ${isZoomed ? 'pp--zoomed' : ''} ${showThreeD ? 'pp--3d' : ''} ${showThreeD && focusField ? 'pp--typing' : ''}`}>
      <div className="pp__header"><Header hideOnScroll={false} showTopBar={false} /></div>
      <div className="pp__header-mobile"><Header hideOnScroll={false} showTopBar={false} /></div>

      <main className="pp__main">
        {showThreeD ? (
          <ThreeDGallery
            threeD={threeD}
            onError={() => setThreeDFailed(true)}
            focusView={threeDFocus.view}
            focusTick={threeDFocus.tick}
            zoomed={isZoomed}
            onZoomChange={setIsZoomed}
          />
        ) : (
        <Gallery
          alt={pkg?.name}
          images={effectiveImages}
          activeImage={activeImage}
          setActiveImage={setActiveImage}
          isZoomed={isZoomed}
          setIsZoomed={setIsZoomed}
          scrollYBeforeZoom={scrollYBeforeZoom}
          galleryRef={galleryRef}
          handleTouchStart={handleTouchStart}
          handleTouchEnd={handleTouchEnd}
        />
        )}

        <div className="pp__configurator">
          <div className="cfg-heading-lg">
            <div className="cfg-heading-lg__top">
              <h1 className="cfg-heading-lg__title typo-h2-editorial">{pkg.name}</h1>
              <Price type="regular" state="included" price={pkg.price} />
            </div>
            {pkg.description && (
              <div className="cfg-heading-lg__description typo-body-sm" style={{ whiteSpace: 'pre-wrap' }}>{pkg.description}</div>
            )}
          </div>

          {readOnly ? (
            <SchoolClassGate
              brandSlug={BRAND_SLUG}
              storageKey={STORAGE_KEYS.clsfi}
              prefill={routeSchoolSlug ? { schoolSlug: routeSchoolSlug, shopSlug } : null}
              onSelected={handleGateSelected}
              title="Valitse koulusi ja luokkasi"
              text="Näytämme hintasi, ja voit muokata ja tilata paketin."
              schoolLabel="Koulu"
              shopLabel="Kauppa"
              classLabel="Luokka"
              classOptionLabel={fiClassLabel}
              buttonLabel="Jatka"
              busyLabel="Hetki…"
            />
          ) : (
          <>
          <div className="pkg__products-section">
            <HeadingMedium title="Tuotteet" />
            <div className="pkg__type-cards">
              {groups.map((group, i) => (
                <ProductCard
                  key={i}
                  image={group.products[0]?.images?.[0]?.src}
                  imageAlt={group.name}
                  title={group.name.toUpperCase()}
                  selected={i === activeTypeIndex}
                  selectedLabel="VALITTU"
                  onClick={() => setActiveTypeIndex(i)}
                  className="pkg__type-card"
                />
              ))}
            </div>
          </div>

          {activeGroup && (
            <div className="pkg__sub-option">
              <HeadingSmall title={activeGroup.name.toUpperCase()} showSelection={false} />
              <Input
                type="dropdown"
                label="Valitse tuote"
                placeholder="Valitse tuote"
                showIcon={false}
                value={selectedProduct?.name ?? ''}
                showOptionIcons={false}
                options={productOptions}
                error={validationErrors[activeTypeIndex]?.product || ''}
                onChange={(e) => handleProductSelect(e.target ? e.target.value : e.value || e)}
                onSelect={(option) => option && handleProductSelect(option.value)}
              />
            </div>
          )}

          {selectedProduct && (
            <div className="pkg__product-config pp__addons-reveal">
              <AttributeSelector
                colors={colors}
                sizes={sizes}
                attributes={attributes}
                selectedColor={config.selectedColor}
                selectedSize={config.selectedSize}
                selectedAttributes={config.selectedAttributes}
                onColorChange={(i) => { updateConfig('selectedColor', i); focusThreeD('color'); }}
                onSizeChange={(s) => { updateConfig('selectedSize', s); clearError(activeTypeIndex, 'size'); }}
                onAttributeChange={handleAttributeChange}
                onAttributeFocus={focusThreeD}
                openFocusMode={openFocusMode}
                isMobile={isMobile}
                onAttributeBlur={blurThreeD}
                sizeError={validationErrors[activeTypeIndex]?.size || ''}
                labels={{ color: 'Väri', size: 'Koko', sizeGuide: 'Kokotaulukko', textfieldError: 'Teksti on liian pitkä tai sisältää virheellisiä merkkejä', requiredError: 'Täytä tämä kenttä' }}
                onSizeGuideOpen={() => setIsSizeGuideOpen(true)}
                disabledColorIdxs={disabledColorIdxs}
                pinnedColorIdxs={pinnedColorIdxs}
                disabledSizes={disabledSizes}
                disabledValueIdsByAttribute={disabledValueIdsByAttribute}
                soldOutColorIdxs={soldOutColorIdxs}
                soldOutSizes={soldOutSizes}
                soldOutValueIdsByAttribute={soldOutValueIdsByAttribute}
                hiddenControlSlugs={hiddenControlSlugs}
                excludeNonColorExtras
              />

              <CLSAddons
                visible={config.selectedColor !== null && config.selectedSize !== null}
                dynamicAttributes={attributes}
                selectedAttributes={config.selectedAttributes}
                onAttributeChange={handleAttributeChange}
                onAttributeFocus={focusThreeD}
                openFocusMode={openFocusMode}
                isMobile={isMobile}
                onAttributeBlur={blurThreeD}
                onInfoOpen={(html, title) => setAttrInfo({ html, title })}
                disabledValueIdsByAttribute={disabledValueIdsByAttribute}
                hiddenControlSlugs={hiddenControlSlugs}
                unavailableLinked={unavailableLinked}
                showRequiredErrors={showRequiredErrors}
                requiredEmptySlugs={new Set(activeUnansweredRevealed)}
              />
            </div>
          )}

          {packageAttributes.length > 0 && (
            <div className="pkg__package-attrs pp__addons-reveal pp__options">
              <HeadingMedium title="Lisävalinnat" />
              <DynamicAttributeControls
                attributes={packageAttributes}
                selectedAttributes={selectedPackageAttributes}
                onAttributeChange={(slug, val) => setSelectedPackageAttributes((prev) => ({ ...prev, [slug]: val }))}
                onInfoOpen={(html, title) => setAttrInfo({ html, title })}
                disabledValueIdsByAttribute={{}}
              />
            </div>
          )}
          </>
          )}

          {/* "Osta riskittömästi" block not shown on CLS FI (aligned with DK, 2026-10-14). */}
          {!editOrder && <DeliveryBlock time="Rahtivapaa toimitus" cost="" description="" />}
        </div>
      </main>

      {!readOnly && (
      <StickyFooter
        buttonSuccess={addSuccess}
        price={`${totalPriceRaw} €`}
        priceError={priceMismatch}
        targetText={priceLocked && targetPrice != null ? `Tulee olla ${targetPrice} €` : null}
        deliveryTime="Rahtivapaa toimitus"
        deliveryPrice=""
        deliveryText=""
        showDelivery={!editOrder}
        buttonLabel={anySelectionSoldOut ? 'Loppuunmyyty' : ((editOrder || editCart) ? 'Päivitä' : 'Lisää ostoskoriin')}
        buttonDisabled={priceMismatch || anySelectionSoldOut}
        onButtonClick={async () => {
          if (!validate()) return;
          if (priceMismatch) return;

          if (editOrder) {
            navigate(`/clothing/cls-fi/edit-order/${editOrder.orderNumber}`, {
              state: { updatedItem: { itemId: editOrder.itemId, section: editOrder.section, includedProducts: buildIncludedProducts() } },
            });
            return;
          }

          if (editCart) {
            // Replace the cart line: remove FIRST (the BE merges identical
            // lines on add), then re-add; restore the original on failure.
            if (addingToCart) return;
            setAddingToCart(true);
            try {
              const image = pkg.images?.[0]?.src || pkg.images?.[0]?.url || effectiveImages[0]?.src || effectiveImages[0]?.url || null;
              const payload = {
                itemType: 'package',
                packageId: pkg.id,
                quantity: editCart.quantity || 1,
                unitPrice: pkg.priceRaw ?? 0,
                additionalPrice: attributesAdditional + packageAttrsAdditional,
                options: { name: pkg.name, slug: slug || editCart.options?.slug || null, image, includedProducts: buildIncludedProducts(), packageAttributes: selectedPackageAttributes },
              };
              await removeItem(editCart.itemId);
              try {
                await addItem(payload);
              } catch (err) {
                try {
                  await addItem({ itemType: 'package', packageId: pkg.id, quantity: editCart.quantity || 1, options: editCart.options || {} });
                } catch { /* restore is best-effort */ }
                throw err;
              }
              navigate('/clothing/cls-fi/shopping-cart');
            } catch (err) {
              console.error('Cart package update failed', err);
            } finally {
              setAddingToCart(false);
            }
            return;
          }

          const identification = requireIdentification();
          if (identification === null) return; // popup opened — no add without it
          if (addingToCart) return;
          setAddingToCart(true);
          try {
            // Bind the FULL scope onto a school-less cart before the first
            // add, so checkout never guesses the shop (two-open-shops schools) —
            // and the class onto an empty cart that only knows the school
            // (services/cartScope.js).
            const scope = cartScopeToBind({ cartSchool, itemCount: cartItemCount, identification, includeShop: true });
            if (scope) {
              await Promise.resolve(setCartSchool(scope)).catch(() => {});
            }
            // Main cart thumbnail = the package's own general image (fall back to
            // the current product gallery image only if the package has none).
            const image = pkg.images?.[0]?.src || pkg.images?.[0]?.url || effectiveImages[0]?.src || effectiveImages[0]?.url || null;
            await addItem({
              itemType: 'package',
              packageId: pkg.id,
              unitPrice: pkg.priceRaw ?? 0,
              additionalPrice: attributesAdditional + packageAttrsAdditional,
              options: { name: pkg.name, slug, image, includedProducts: buildIncludedProducts(), packageAttributes: selectedPackageAttributes },
            });
            // Success: brief checkmark on the button + a 1s ease-in-out scroll to top.
            setAddSuccess(true);
            smoothScrollToTop(1000);
            setTimeout(() => setAddSuccess(false), 2000);
            document.dispatchEvent(new CustomEvent('open-minicart'));
          } catch (err) {
            console.error('Add package to cart failed', err);
          } finally {
            setAddingToCart(false);
          }
        }}
        className={`pp__footer ${focusField ? 'pp__footer--hidden' : ''}`}
      />
      )}

      <SizeGuide isOpen={isSizeGuideOpen} onClose={() => setIsSizeGuideOpen(false)} title={SIZE_GUIDE_TITLE} items={SIZE_GUIDE_ITEMS} />

      <InfoTooltip isOpen={!!attrInfo} onClose={() => setAttrInfo(null)} ariaLabel={attrInfo?.title || ''}>
        {attrInfo && (
          <div className="pp-attr-hint typo-body" dangerouslySetInnerHTML={{ __html: attrInfo.html }} />
        )}
      </InfoTooltip>

      {/* MOBILE FOCUS MODAL */}
      {isMobile && (
        <MobileFocusModal
          focusField={focusField}
          activeFocusData={activeFocusData}
          closeFocusMode={closeFocusMode}
          containerRef={activeFocusContainerRef}
        />
      )}
    </div>
  );
};

export default ProductPackage;
